import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    areIdsEqual,
    buildSceneModel,
    getAvailableScanTypes,
    getEffectiveScanType,
    getModelToggleState,
    getSceneModelId,
    getSceneVariantId,
    getScanDisplayName,
    isRuntimeStateFailed,
    quickViewButtons,
    reconcileIds,
    reconcileRequestedVariantIds,
    scanTypeButtons,
} from './viewerStateUtils';

function useViewerSceneState({
    resourceScopeId,
    scans,
    showTips,
    setShowTips,
}) {
    const [interactionMode, setInteractionMode] = useState('camera');
    const [globalScanType, setGlobalScanType] = useState('ply');
    const [showBottomDock, setShowBottomDock] = useState(true);
    const [showQuickViews, setShowQuickViews] = useState(true);
    const [showTexture, setShowTexture] = useState(true);
    const [visibleScanIds, setVisibleScanIds] = useState([]);
    const [requestedBuildVariantIds, setRequestedBuildVariantIds] = useState([]);
    const [activeCameraPreset, setActiveCameraPreset] = useState(null);
    const [cameraCommand, setCameraCommand] = useState(null);
    const [cameraViewState, setCameraViewState] = useState({
        canResetView: false,
    });
    const [modelRuntimeStateById, setModelRuntimeStateById] = useState({});
    const knownScanIdsRef = useRef(new Set());
    const cameraCommandNonceRef = useRef(0);

    const scanEntries = useMemo(
        () => scans.map((scan, index) => {
            const scanId = getSceneModelId(scan, index);
            const availableScanTypes = getAvailableScanTypes(scan);
            const selectedScanType = getEffectiveScanType(scan, globalScanType);

            return {
                availableScanTypes,
                index,
                scan,
                scanId,
                selectedScanType,
                selectedVariantId: selectedScanType ? getSceneVariantId(scanId, selectedScanType) : null,
            };
        }),
        [globalScanType, scans],
    );

    const availableScanIds = useMemo(
        () => scanEntries
            .filter((entry) => entry.availableScanTypes.length > 0)
            .map((entry) => entry.scanId),
        [scanEntries],
    );

    const defaultVisibleScanIds = useMemo(
        () => scanEntries
            .filter((entry) => entry.availableScanTypes.length > 0 && entry.scan.defaultVisible !== false)
            .map((entry) => entry.scanId),
        [scanEntries],
    );

    const availableScanIdsKey = useMemo(
        () => availableScanIds.join('|'),
        [availableScanIds],
    );

    const allVariantIds = useMemo(
        () => scanEntries.flatMap((entry) => (
            entry.availableScanTypes.map((scanType) => getSceneVariantId(entry.scanId, scanType))
        )),
        [scanEntries],
    );

    const availableGlobalScanTypes = useMemo(
        () => scanTypeButtons.filter((scanType) => (
            scanEntries.some((entry) => entry.availableScanTypes.includes(scanType))
        )),
        [scanEntries],
    );

    const availableScanIdSet = useMemo(
        () => new Set(availableScanIds),
        [availableScanIds],
    );

    const visibleScanIdSet = useMemo(
        () => new Set(visibleScanIds),
        [visibleScanIds],
    );

    const requestedBuildVariantIdSet = useMemo(
        () => new Set(requestedBuildVariantIds),
        [requestedBuildVariantIds],
    );

    const sceneModels = useMemo(
        () => scanEntries.flatMap((entry) => (
            entry.availableScanTypes
                .map((scanType) => buildSceneModel(
                    entry.scan,
                    entry.index,
                    scanType,
                    requestedBuildVariantIdSet.has(getSceneVariantId(entry.scanId, scanType)),
                ))
                .filter(Boolean)
        )),
        [requestedBuildVariantIdSet, scanEntries],
    );

    const failedScanIds = useMemo(
        () => scanEntries
            .filter((entry) => (
                entry.availableScanTypes.length > 0
                && entry.availableScanTypes.every((scanType) => (
                    isRuntimeStateFailed(modelRuntimeStateById[getSceneVariantId(entry.scanId, scanType)])
                ))
            ))
            .map((entry) => entry.scanId),
        [modelRuntimeStateById, scanEntries],
    );

    const failedScanIdSet = useMemo(
        () => new Set(failedScanIds),
        [failedScanIds],
    );

    const visibleScanEntries = useMemo(
        () => scanEntries
            .filter((entry) => visibleScanIdSet.has(entry.scanId))
            .map((entry) => {
                const runtimeState = entry.selectedVariantId
                    ? modelRuntimeStateById[entry.selectedVariantId] || null
                    : null;
                const supportsTexture = runtimeState?.supportsTexture
                    ?? (runtimeState?.textureStatus === 'failed'
                        ? false
                        : entry.selectedScanType === 'ply' && Boolean(entry.scan.textureUrl));
                const supportsColor = runtimeState?.supportsColor
                    ?? (entry.selectedScanType === 'stl' || entry.selectedScanType === 'ply');
                const textureEligible = entry.selectedScanType === 'ply' && supportsTexture;
                const textureVisible = textureEligible && showTexture;

                return {
                    ...entry,
                    isSwitchable: entry.availableScanTypes.includes('stl') && entry.availableScanTypes.includes('ply'),
                    runtimeState,
                    supportsColor,
                    supportsTexture,
                    textureEligible,
                    textureVisible,
                };
            }),
        [modelRuntimeStateById, scanEntries, showTexture, visibleScanIdSet],
    );

    const visibleVariantIds = useMemo(
        () => scanEntries
            .map((entry) => (visibleScanIdSet.has(entry.scanId) ? entry.selectedVariantId : null))
            .filter(Boolean),
        [scanEntries, visibleScanIdSet],
    );

    const hasAnySceneModels = sceneModels.length > 0;
    const hasNoRetrievedScans = scans.length === 0;
    const hasNoSceneSources = !hasNoRetrievedScans && !hasAnySceneModels;
    const haveAllSceneModelsFailed = hasAnySceneModels
        && sceneModels.every((model) => isRuntimeStateFailed(modelRuntimeStateById[model.id]));

    const sceneEmptyState = useMemo(() => {
        if (hasNoRetrievedScans) {
            return {
                body: 'This case does not have any scans yet.',
                title: 'No Scans Available',
            };
        }

        if (hasNoSceneSources) {
            return {
                body: 'This case does not have any 3D scan files that can be opened in the viewer.',
                title: 'No 3D Scans Available',
            };
        }

        if (haveAllSceneModelsFailed) {
            return {
                body: 'The available scan files could not be loaded. Try reopening the viewer, loading a different case, or refreshing the webpage.',
                title: 'Unable To Load Scans',
            };
        }

        return null;
    }, [hasNoRetrievedScans, hasNoSceneSources, haveAllSceneModelsFailed]);

    useEffect(() => {
        const defaultVisibleScanIdSet = new Set(defaultVisibleScanIds);
        const newlyVisibleScanIds = availableScanIds.filter(
            (scanId) => (
                !knownScanIdsRef.current.has(scanId)
                && defaultVisibleScanIdSet.has(scanId)
            ),
        );

        setVisibleScanIds((currentVisibleScanIds) => {
            const currentVisibleScanIdSet = new Set(currentVisibleScanIds);
            const nextVisibleScanIds = availableScanIds.filter((scanId) => currentVisibleScanIdSet.has(scanId));

            nextVisibleScanIds.push(...newlyVisibleScanIds);

            return reconcileIds(currentVisibleScanIds, nextVisibleScanIds);
        });

        availableScanIds.forEach((scanId) => {
            knownScanIdsRef.current.add(scanId);
        });
    }, [availableScanIds, defaultVisibleScanIds]);

    useEffect(() => {
        if (failedScanIds.length === 0) {
            return;
        }

        setVisibleScanIds((currentVisibleScanIds) => {
            const nextVisibleScanIds = currentVisibleScanIds.filter(
                (scanId) => !failedScanIdSet.has(scanId),
            );

            return reconcileIds(currentVisibleScanIds, nextVisibleScanIds);
        });
    }, [failedScanIdSet, failedScanIds.length]);

    useEffect(() => {
        if (!availableGlobalScanTypes.length || availableGlobalScanTypes.includes(globalScanType)) {
            return;
        }

        setGlobalScanType(availableGlobalScanTypes[0]);
    }, [availableGlobalScanTypes, globalScanType]);

    useEffect(() => {
        setRequestedBuildVariantIds((currentRequestedBuildVariantIds) => reconcileRequestedVariantIds(
            currentRequestedBuildVariantIds,
            allVariantIds,
            visibleVariantIds,
        ));
    }, [allVariantIds, visibleVariantIds]);

    useEffect(() => {
        setActiveCameraPreset(null);
    }, [availableScanIdsKey]);

    const sceneAppearance = useMemo(
        () => ({
            defaultAppearance: {
                showTexture,
            },
            modelAppearances: {},
        }),
        [showTexture],
    );

    const visibleSwitchableEntries = useMemo(
        () => visibleScanEntries.filter((entry) => entry.isSwitchable),
        [visibleScanEntries],
    );

    const visibleTextureEligibleEntries = useMemo(
        () => visibleScanEntries.filter((entry) => entry.textureEligible),
        [visibleScanEntries],
    );

    const hasVisibleScanOverrides = useMemo(
        () => !areIdsEqual(
            availableScanIds.filter((scanId) => visibleScanIdSet.has(scanId)),
            defaultVisibleScanIds,
        ),
        [availableScanIds, defaultVisibleScanIds, visibleScanIdSet],
    );

    const canResetScans = hasVisibleScanOverrides;

    const modelToggleState = useMemo(
        () => getModelToggleState(visibleSwitchableEntries, globalScanType),
        [globalScanType, visibleSwitchableEntries],
    );

    const nextModelToggleType = globalScanType === 'ply' ? 'stl' : 'ply';

    const queueCameraCommand = (type) => {
        if (!hasAnySceneModels) {
            return;
        }

        cameraCommandNonceRef.current += 1;
        setCameraCommand({
            nonce: cameraCommandNonceRef.current,
            type,
        });

        if (type === 'resetView') {
            setActiveCameraPreset(null);
            return;
        }

        setActiveCameraPreset(type);
    };

    const handleViewportCameraInteraction = () => {
        setActiveCameraPreset((currentPreset) => (currentPreset == null ? currentPreset : null));
    };

    const toggleSceneModelVisibility = (scanId) => {
        if (!availableScanIdSet.has(scanId) || failedScanIdSet.has(scanId)) {
            return;
        }

        setVisibleScanIds((currentVisibleScanIds) => {
            if (currentVisibleScanIds.includes(scanId)) {
                return currentVisibleScanIds.filter((visibleId) => visibleId !== scanId);
            }

            return [
                ...currentVisibleScanIds,
                scanId,
            ];
        });
    };

    const handleGlobalScanTypeToggle = () => {
        if (modelToggleState === 'disabled') {
            return;
        }

        const nextScanType = nextModelToggleType;

        setGlobalScanType(nextScanType);
        setRequestedBuildVariantIds((currentRequestedBuildVariantIds) => reconcileIds(
            currentRequestedBuildVariantIds,
            [
                ...currentRequestedBuildVariantIds,
                ...visibleSwitchableEntries.map((entry) => getSceneVariantId(entry.scanId, nextScanType)),
            ],
        ));
    };

    const handleResetAllScans = () => {
        if (!canResetScans) {
            return;
        }

        setVisibleScanIds(defaultVisibleScanIds);
    };

    const handleLaserModeToggle = () => {
        if (!hasAnySceneModels) {
            return;
        }

        setInteractionMode((currentInteractionMode) => {
            const nextInteractionMode = currentInteractionMode === 'laser' ? 'camera' : 'laser';

            if (nextInteractionMode === 'laser') {
                setShowQuickViews(true);
            }

            return nextInteractionMode;
        });
    };

    const canUseSceneControls = hasAnySceneModels;
    const isLaserMode = interactionMode === 'laser';
    const areBottomDockControlsEnabled = canUseSceneControls && showBottomDock;
    const isQuickViewStripOpen = canUseSceneControls && showBottomDock && showQuickViews;
    const canToggleModel = areBottomDockControlsEnabled && modelToggleState !== 'disabled';
    const canToggleTexture = areBottomDockControlsEnabled && visibleTextureEligibleEntries.length > 0;
    const canToggleLaser = areBottomDockControlsEnabled;
    const modelToggleTitle = modelToggleState === 'disabled'
        ? 'No visible scans can switch between STL and PLY'
        : modelToggleState === 'stl'
            ? 'Global model preference is STL. Click to prefer PLY when available.'
            : 'Global model preference is PLY. Click to prefer STL when available.';
    const quickViewsToggleTitle = !areBottomDockControlsEnabled
        ? 'Quick view presets unavailable'
        : showQuickViews
            ? 'Hide quick view presets'
            : 'Show quick view presets';
    const resetViewTitle = !areBottomDockControlsEnabled
        ? 'Reset view unavailable'
        : cameraViewState.canResetView
            ? 'Reset camera to the default framing for visible scans'
            : 'Camera already matches the default framing for visible scans';
    const textureToggleTitle = !canToggleTexture
        ? 'No visible scans can use texture right now'
        : showTexture
            ? 'Disable textures for all applicable visible scans'
            : 'Enable textures for all applicable visible scans';
    const laserToggleTitle = !canToggleLaser
        ? 'Laser pointer unavailable'
        : isLaserMode
            ? 'Disable laser pointer mode'
            : 'Enable laser pointer mode';

    const scanRows = useMemo(
        () => scanEntries.map((entry) => {
            const {
                availableScanTypes,
                index,
                scanId,
            } = entry;
            const hasLoadFailure = failedScanIdSet.has(scanId);
            const isAvailable = availableScanTypes.length > 0 && !hasLoadFailure;
            const isChecked = visibleScanIds.includes(scanId);
            const scanDisplayName = getScanDisplayName(entry.scan, `View ${index + 1}`);
            const unavailableTitle = hasLoadFailure ? `${scanDisplayName} could not be loaded` : undefined;

            return {
                hasLoadFailure,
                id: scanId,
                isAvailable,
                isChecked,
                label: scanDisplayName,
                onToggleVisible: () => toggleSceneModelVisibility(scanId),
                statusText: hasLoadFailure ? 'Failed to load' : null,
                title: unavailableTitle,
            };
        }),
        [failedScanIdSet, scanEntries, visibleScanIds],
    );

    const quickViews = useMemo(
        () => quickViewButtons.map((button) => ({
            disabled: !canUseSceneControls || !showQuickViews || !showBottomDock,
            id: button.key,
            isActive: activeCameraPreset === button.key && canUseSceneControls,
            label: button.label,
            onClick: () => queueCameraCommand(button.key),
            title: activeCameraPreset === button.key
                ? `${button.label} view preset active`
                : `Set camera to ${button.label} view`,
            view: button.key,
        })),
        [activeCameraPreset, canUseSceneControls, showBottomDock, showQuickViews],
    );

    return {
        bottomDockState: {
            activeCameraPreset,
            isQuickViewStripOpen,
            laserToggle: {
                active: isLaserMode && canToggleLaser,
                ariaLabel: laserToggleTitle,
                ariaPressed: isLaserMode,
                disabled: !canToggleLaser,
                onClick: handleLaserModeToggle,
                title: laserToggleTitle,
            },
            modelToggle: {
                ariaLabel: modelToggleTitle,
                disabled: !canToggleModel,
                onClick: handleGlobalScanTypeToggle,
                state: modelToggleState,
                title: modelToggleTitle,
            },
            quickViews,
            quickViewsToggle: {
                active: showQuickViews && canUseSceneControls,
                ariaLabel: quickViewsToggleTitle,
                ariaPressed: showQuickViews,
                disabled: !areBottomDockControlsEnabled,
                onClick: () => setShowQuickViews((currentValue) => !currentValue),
                title: quickViewsToggleTitle,
            },
            resetView: {
                disabled: !areBottomDockControlsEnabled || !cameraViewState.canResetView,
                onClick: () => queueCameraCommand('resetView'),
                title: resetViewTitle,
            },
            showBottomDock,
            textureToggle: {
                active: showTexture && canToggleTexture,
                ariaLabel: textureToggleTitle,
                ariaPressed: showTexture,
                disabled: !canToggleTexture,
                onClick: () => setShowTexture((currentValue) => !currentValue),
                title: textureToggleTitle,
            },
            toggleCollapse: () => setShowBottomDock((currentValue) => !currentValue),
        },
        sceneEmptyState,
        sceneViewportProps: {
            appearance: sceneAppearance,
            cameraCommand,
            interactionMode,
            onCameraViewStateChange: setCameraViewState,
            onRuntimeModelStateChange: setModelRuntimeStateById,
            onUserCameraInteraction: handleViewportCameraInteraction,
            resourceScopeId,
            sceneModels,
            visibleModelIds: visibleVariantIds,
        },
        scansPanelState: {
            canResetScans,
            onResetAllScans: handleResetAllScans,
            resetButtonLabel: 'Reset',
            resetButtonTitle: canResetScans
                ? 'Reset scan visibility to the default set'
                : 'Scan visibility already matches the default state',
            rows: scanRows,
            title: 'Scans',
        },
        tipsState: {
            isLaserMode,
            onToggle: () => setShowTips(!showTips),
            showTips,
        },
    };
}

export default useViewerSceneState;
